import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  useAddLabTest,
  useEditLabTest,
  useLabTest,
  useLabTests,
  useTestDiscounts,
  useTests,
} from "api/tests";
import Discount from "classes/discount";
import PageTitle from "generics/PageTitle";
import ToolbarPortal from "generics/ToolbarPortal";
import { DiscountCard, DiscountSection } from "modules/discounts/DiscountPage";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaPlus as AddIcon } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";

const defaultTest = {
  fee: 100,
};

export default function AddTest({
  edit,
  onCancel,
}: {
  edit?: boolean | undefined;
  onCancel?: any;
}) {
  const testsQuery = useTests();
  const labTestsQuery = useLabTests();
  const navigate = useNavigate();
  const [addTest, addTestInfo] = useAddLabTest();
  const [editTest, editTestInfo] = useEditLabTest();
  const { testId } = useParams();
  const discountsQuery = useTestDiscounts(testId);
  const labTestInfo = useLabTest(testId);

  const [action, setAction] = useState<any>();
  const [selectedDiscount, setSelectedDiscount] = useState<any>();
  const { handleSubmit, register, reset, getValues } = useForm();
  let [selectedTest, setSelectedTest] = useState<any>(defaultTest);
  const [availableTestsMap, setAvailableTestsMap] = useState<any>({});
  const { parentId } = getValues();
  const toast = useToast();

  useEffect(() => {
    const labTestKeys = labTestsQuery.data?.map((test: any) => test.parentId);

    if (testsQuery?.data) {
      testsQuery.data.forEach((test) => {
        if (!labTestKeys?.includes(test.id)) {
          availableTestsMap[test.id] = test;
        }
      });
      setAvailableTestsMap({ ...availableTestsMap });
    }
    // eslint-disable-next-line
  }, [testsQuery?.data, labTestsQuery?.data]);

  useEffect(() => {
    if (!parentId && !edit && !selectedTest?.id) {
      if (Object.values(availableTestsMap).length !== 0) {
        setSelectedTest(Object.values(availableTestsMap)[0]);
      }
    }
    // eslint-disable-next-line
  }, [availableTestsMap, parentId]);

  useEffect(() => {
    if (labTestInfo.data) {
      setSelectedTest(labTestInfo.data);
      reset({ fee: labTestInfo.data.fee, parentId: labTestInfo.data.parentId });
    }
    // eslint-disable-next-line
  }, [labTestInfo.data, testsQuery.data]);

  async function onSubmit(values: any) {
    let { fee } = values;
    selectedTest.fee = +fee || 0;

    const discountOfFee = Math.floor((90 / 100) * fee);

    const discount = discountsQuery.data?.find(
      (discountQuery) => (discountQuery?.flatDiscount ?? 0) > discountOfFee
    );

    if (discount) {
      toast({
        status: "error",
        title: "Test Update Failure",
        description:
          "The discount applied in this test has more value than the price of test ",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (edit) {
        await editTest({
          fee: +fee,
          testId: selectedTest.id,
        });
      } else {
        if (!values.parentId) {
          toast({
            status: "error",
            title: "Test Required",
            description: "Please select a test to proceed",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const parentTest = testsQuery?.data?.find((parentTest) => {
          return parentTest.id === values.parentId;
        });
        if (!parentTest) {
          toast({
            status: "error",
            title: "Test Required",
            description: "Corresponding Test not found with the selected test",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const parentImports = {
          ...parentTest,
          id: undefined,
          parentId: parentTest.id,
        };
        await addTest({
          ...selectedTest,
          ...parentImports,
        });

        delete availableTestsMap[parentTest.id];
        setAvailableTestsMap({
          ...availableTestsMap,
        });
      }
      navigate("/tests");
    } catch (e) {
      toast({
        status: "error",
        title: "Error !!",
        description: e.message,
        duration: 3000,
        isClosable: true,
      });
    }
  }

  if (labTestInfo.isLoading) {
    return <Spinner />;
  }

  if (testId && labTestInfo.status === "success" && !labTestInfo.data) {
    navigate("/tests");
    return <div></div>;
  }

  return (
    <Flex direction="column" padding={edit ? "8" : "0"}>
      <DiscountsModal
        selectedTest={selectedTest}
        action={action}
        discount={selectedDiscount}
        onClose={() => {
          setAction(undefined);
          setSelectedDiscount(undefined);
        }}
        setSelectedDiscount={setSelectedDiscount}
      />
      {edit && (
        <ToolbarPortal>
          <Flex justifyContent="space-between" alignItems="center">
            <Flex justify="space-between" flexGrow={1} align="center">
              <PageTitle flexGrow={1}>
                {edit ? `Update ${selectedTest?.name}` : "Add New Test"}
              </PageTitle>
            </Flex>
          </Flex>
        </ToolbarPortal>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column">
          <Flex>
            <Flex flex={3} direction="column">
              <FormControl>
                <FormLabel as="legend">Select Test</FormLabel>
                <Select
                  ref={register()}
                  name="parentId"
                  required
                  disabled={edit}
                  onChange={(e: any) => {
                    setSelectedTest(availableTestsMap[e.target.value]);
                  }}
                >
                  {edit ? (
                    <option value={selectedTest.parentId} selected>
                      {
                        testsQuery.data?.find(
                          (i) => i.id === selectedTest.parentId
                        )?.name
                      }
                    </option>
                  ) : (
                    Object.values(availableTestsMap)
                      ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
                      ?.map((test: any) => {
                        return (
                          <option value={test.id} key={test.id}>
                            {test.name}
                          </option>
                        );
                      })
                  )}
                </Select>
              </FormControl>
              <Flex>
                <Text
                  flex={2}
                  fontSize="12px"
                  color="settingText.500"
                  fontWeight="400"
                >
                  {selectedTest?.description}
                </Text>
              </Flex>

              <Text
                color={"highlight"}
                flex={2}
                fontSize="13px"
                fontWeight="400"
              >
                {selectedTest?.labCollector
                  ? "*Special collection required."
                  : ""}
              </Text>
            </Flex>
            <FormControl flex={1} marginLeft={6}>
              <FormLabel
                as="legend"
                fontSize="md"
                color="highlight.500"
                fontWeight="medium"
              >
                Price (In Rupees)
              </FormLabel>
              <Input
                type="number"
                borderRadius="4px"
                placeholder="Enter fee for the test"
                defaultValue={selectedTest.fee}
                name="fee"
                ref={register()}
                maxLength={2}
                required
              />
            </FormControl>
          </Flex>
        </Flex>

        <Flex marginY="4">
          <Button
            mr={3}
            type="submit"
            isLoading={addTestInfo.isLoading || editTestInfo.isLoading}
          >
            {edit ? "Update Test" : "Add Test"}
          </Button>
        </Flex>
      </form>

      {discountsQuery.data && (
        <Flex direction="column" paddingY="2">
          <Text fontSize="md" color={"highlight"} fontWeight="medium">
            Upcoming Discounts
          </Text>
          <Flex justify="space-between" marginY="1" flexGrow={1}>
            <Flex width="100%">
              <Flex flexGrow={1}>
                {discountsQuery.data && (
                  <Flex
                    border="1px solid"
                    padding="6px 8px"
                    borderColor="borderHighlight.500"
                    backgroundColor="borderHighlight.100"
                    flexGrow={1}
                    borderRadius="3px"
                    color="primary.500"
                  >
                    <Text fontSize="sm" padding={1} color="primary.500">
                      {discountsQuery?.data.length} discounts found
                    </Text>
                  </Flex>
                )}
              </Flex>
              <Button
                ml="2"
                h="auto"
                py="3"
                variant="outline"
                borderColor="border.500"
                color="highlight.500"
                fontWeight="500"
                leftIcon={<AddIcon size="14"></AddIcon>}
                onClick={() => {
                  setAction("discount");
                  setSelectedDiscount(new Discount({}));
                }}
              >
                Add Discount
              </Button>
            </Flex>
          </Flex>
          {discountsQuery.isLoading && <Text>Loading ... </Text>}
          {discountsQuery.data && (
            <Flex direction="column">
              {discountsQuery.data.map((discount: any, index: number) => {
                return (
                  <DiscountCard
                    key={index}
                    discount={discount}
                    editDiscount={(discount: any) => {
                      setSelectedDiscount(discount);
                      setAction("discount");
                    }}
                    deleteDiscount={(discount: any) => {
                      setAction("delete");
                      setSelectedDiscount(discount);
                    }}
                    showEdit={true}
                  ></DiscountCard>
                );
              })}
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}

function DiscountsModal({
  onClose,
  selectedTest: test,
  setSelectedDiscount,
  discount,
  action,
}: any) {
  const cancelRef = React.useRef<any>();
  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={discount}
    >
      <AlertDialogOverlay />

      <AlertDialogContent maxW="50vw">
        <Flex direction="column">
          <DiscountSection
            discount={discount}
            newDiscount={() => {
              setSelectedDiscount(new Discount({}));
            }}
            test={test}
            showDiscounts={!discount}
            editDiscount={(discount: any) => {
              setSelectedDiscount(discount);
            }}
            action={action}
            onClose={onClose}
          />
        </Flex>
      </AlertDialogContent>
    </AlertDialog>
  );
}
